import { Grid } from '@/components/common/grid';
import { SpinnerIcon, InfoIcon, QuestionIcon } from '@phosphor-icons/react';
import { CoreEnumAutocomplete } from './CoreEnumAutocomplete';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MatchingVariantsInfo } from './MatchingVariantsInfo';
import { useProductAttributeSelection } from './hooks/useProductAttributeSelection';

export function ProductAttributeSelection({
  selectedProductId,
  selectedVariantId,
  onSelectVariant,
  customerId,
}: {
  selectedProductId: string;
  selectedVariantId?: string;
  onSelectVariant: (variantId: string) => void;
  customerId?: string;
}) {
  const {
    selectedAttributes,
    selectedAttributeOptionsByAttrId,
    visibleAttributes,
    hiddenAttributes,
    currentProductData,
    isLoading,
    isLoadingFilteredAttributes,
    error,
    handleAttributeChange,
    handleEnumSelectionByAttrId,
  } = useProductAttributeSelection({
    selectedProductId,
    selectedVariantId,
    customerId,
  });

  if (error) {
    return <div className="text-red-500">Error loading product attributes</div>;
  }

  if (!currentProductData?.product?.attributes && !visibleAttributes.length) {
    return null;
  }

  return (
    <Grid item xs={12}>
      {isLoading ? (
        <div className="flex justify-center p-4">
          <SpinnerIcon className="size-6 animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative">
            {isLoadingFilteredAttributes && (
              <div
                role="presentation"
                aria-busy
                className="absolute inset-0 z-10 cursor-progress pointer-events-auto bg-transparent"
              />
            )}
            <Grid container spacing={2}>
              {visibleAttributes.map((attr) => {
                const enumOptions = attr.customField.coreEnum?.options || [];
                const noOptions =
                  attr.customField.fieldType === 'enum' &&
                  enumOptions.length === 0;
                const isDisabled = isLoadingFilteredAttributes || noOptions;

                return (
                  <Grid item xs={12} sm={6} md={4} key={attr.id}>
                    {attr.customField.fieldType === 'enum' &&
                    attr.customField.coreEnum ? (
                      <CoreEnumAutocomplete
                        attribute={attr}
                        onAttributeChange={handleAttributeChange}
                        selectedAttributes={selectedAttributes}
                        selectedAttributeOptionsByAttrId={
                          selectedAttributeOptionsByAttrId
                        }
                        onEnumSelectionByAttrId={handleEnumSelectionByAttrId}
                        disabled={isDisabled}
                      />
                    ) : (
                      <Label className="flex flex-col gap-1">
                        <span className="text-sm font-medium">
                          {attr.customField.fieldName}
                          {attr.isRequired && (
                            <span className="text-red-500">*</span>
                          )}
                        </span>
                        <Input
                          disabled={isLoadingFilteredAttributes}
                          required={attr.isRequired}
                          placeholder={attr.customField.description}
                          value={
                            selectedAttributes[attr.customField.fieldName] || ''
                          }
                          onChange={(e) => {
                            handleAttributeChange(
                              attr.customField.fieldName,
                              e.target.value,
                            );
                          }}
                        />
                      </Label>
                    )}
                  </Grid>
                );
              })}
            </Grid>

            {hiddenAttributes.length > 0 && (
              <div className="mt-2 px-2 flex items-center gap-2">
                <InfoIcon className="size-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Hidden:{' '}
                  {hiddenAttributes
                    .map((a) => a.customField.fieldName)
                    .join(', ')}
                </span>
                <div className="group relative">
                  <QuestionIcon className="size-4 text-muted-foreground cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    Attributes are hidden because no options are available for
                    the current selection and this customer&apos;s registered
                    variants.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Show matching variants based on selected attributes */}
          <div>
            <MatchingVariantsInfo
              selectedProductId={selectedProductId}
              selectedAttributes={selectedAttributes}
              selectedVariantId={selectedVariantId}
              onSelectVariant={onSelectVariant}
              customerId={customerId}
            />
          </div>
        </div>
      )}
    </Grid>
  );
}
